/**
 * Parses the `/org/me` response before permission checks use its values.
 * 1. Reject invalid shapes and unknown roles.
 * 2. Keep known permissions and report unknown permission strings.
 * 3. Reject non-string entries and invalid INTERVIEWER grants.
 * Continue in useCurrentUserPermissions.ts.
 */
// keep-sorted:begin
/** Known Permission values that this frontend can use. */
export const EMPLOYEE_PERMISSION_API_VALUES = [
  'CAN_CREATE_FIRST_PARTY_ACCESS_TOKEN',
  'CAN_CREATE_OAUTH_APP_CONNECTION',
  'CAN_CREATE_THIRD_PARTY_ACCESS_TOKEN',
  'CAN_DELEGATE_TO_SCHEDULING_AGENT',
  'CAN_INTERVIEW',
  'CAN_MANAGE_ADDITIONAL_USER_PERMISSIONS',
  'CAN_MANAGE_INTERNAL_SETTINGS',
  'CAN_MANAGE_ORG_SETTINGS',
  'CAN_SCHEDULE',
  'READ_AUDIT_LOGS',
] as const;
// keep-sorted:end

/** A Permission value known to this frontend. */
export type EmployeePermissionApiValue = (typeof EMPLOYEE_PERMISSION_API_VALUES)[number];

/** Known Employee role values returned by the `/org/me` endpoint. */
export const EMPLOYEE_ROLE_API_VALUES = [
  'UNSPECIFIED',
  'INTERVIEWER',
  'SCHEDULER',
  'ADMIN',
  'OWNER',
  'MODERNLOOP_SYSTEM',
] as const;

/** An Employee role value known to this frontend. */
export type EmployeeRoleApiValue = (typeof EMPLOYEE_ROLE_API_VALUES)[number];

/** Parsed role, known permissions, and forward-compatible unknown permission strings. */
export type CurrentUserPermissionsResponse = {
  role: EmployeeRoleApiValue;
  permissions: EmployeePermissionApiValue[];
  unknownPermissions: readonly string[];
};

/** Returns whether a raw string is a known Permission value. */
const isEmployeePermissionApiValue = (value: string): value is EmployeePermissionApiValue =>
  EMPLOYEE_PERMISSION_API_VALUES.some((permission) => permission === value);

/** Returns whether a raw string is a known Employee role value. */
const isEmployeeRoleApiValue = (value: string): value is EmployeeRoleApiValue =>
  EMPLOYEE_ROLE_API_VALUES.some((role) => role === value);

/**
 * Parses the `/org/me` response into typed role and permission values.
 * Unknown permission strings are reported separately so known grants remain usable.
 */
export function parseCurrentUserPermissionsResponse(response: unknown): CurrentUserPermissionsResponse | null {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const { role, permissions } = response as { role?: unknown; permissions?: unknown };
  if (typeof role !== 'string' || !isEmployeeRoleApiValue(role) || !Array.isArray(permissions)) {
    return null;
  }

  const parsedPermissions: EmployeePermissionApiValue[] = [];
  const unknownPermissions: string[] = [];
  for (const permission of permissions) {
    if (typeof permission !== 'string') {
      return null;
    }

    // The backend can add Permission values before the frontend deploys. Rejecting unknown values locked every OWNER out on 2026-09-17 (BUG-1148).
    if (isEmployeePermissionApiValue(permission)) {
      parsedPermissions.push(permission);
    } else {
      unknownPermissions.push(permission);
    }
  }

  // INTERVIEWER memberships cannot hold grants beyond their role permission.
  if (role === 'INTERVIEWER' && parsedPermissions.some((permission) => permission !== 'CAN_INTERVIEW')) {
    return null;
  }

  return { role, permissions: parsedPermissions, unknownPermissions };
}

/**
 * Returns whether the given permissions include the requested API permission value.
 */
export function hasEmployeePermission(
  permissions: readonly EmployeePermissionApiValue[] | undefined,
  permission: EmployeePermissionApiValue
): boolean {
  return permissions?.includes(permission) ?? false;
}
